//dependencies
import { useAutoAnimate } from "@formkit/auto-animate/react";
import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { faPencil } from "@fortawesome/pro-solid-svg-icons";

import { Text } from "@performyard/py-components";

//components
import Icon from "../../../../../../../../components/Icon/Icon";

//store
import { useActions, useInvalidNameMessage, useSetters, useSurveyName } from "../../store/store";

import { useSurveyAccessTier } from "../../../../hooks/useSurveyAccessTier";

import { MeasureCanvas } from "../../../../../../../../utils/textMeasurement/MeasureCanvas";
import { buildFontTemplate } from "../../../../../../../../utils/textMeasurement/textMeasurement";

interface TitleInputProps {
	defaultTitle: string;
}

const TitleInput = (props: TitleInputProps) => {
	const { defaultTitle } = props;
	// store
	const name = useSurveyName();
	const invalidNameMessage = useInvalidNameMessage();
	const actions = useActions();
	const setters = useSetters();
	const { surveyId = "" } = useParams();
	const surveyUserAccess = useSurveyAccessTier(surveyId).tier;
	const [isEditingName, setIsEditingName] = useState(false);
	const [ref] = useAutoAnimate();
	const fontString = buildFontTemplate("sub-heading", "semi-bold");
	const editWidth = new MeasureCanvas().getTextWidth(name, fontString);

	const FormTitle = (
		<form
			ref={ref}
			onSubmit={(e) => {
				e.preventDefault();
				if (actions._validateName(defaultTitle)) {
					setIsEditingName(false);
				}
			}}
			onBlur={(e) => {
				e.preventDefault();
				if (actions._validateName(defaultTitle)) {
					setIsEditingName(false);
				}
			}}
		>
			<div className="group flex w-full items-center gap-[8px] text-neutral-9">
				<input
					autoFocus
					disabled={surveyUserAccess !== "edit"}
					className="w-max-full border-0 border-b-[0.5px] border-neutral-9 p-0 text-sub-heading font-semi-bold text-neutral-9 outline-hidden hover:cursor-text focus:border-primary-4 active:border-primary-4 disabled:border-0 disabled:border-b-[0.5px] disabled:bg-transparent disabled:text-neutral-9"
					style={{
						width: editWidth, // Dynamic width calculation
						minWidth: "200px",
						maxWidth: "800px",
					}}
					value={name}
					onChange={(e) => setters.setSurveyName(e.target.value)}
					data-testid="edit-form-title-input"
				/>
				<button
					className="border-0 bg-transparent p-0"
					type="submit"
					aria-label="Save survey title"
					disabled={surveyUserAccess !== "edit"}
				>
					<Icon
						className="text-neutral-5 hover:cursor-pointer hover:text-neutral-7 disabled:cursor-not-allowed disabled:text-neutral-3"
						size="small"
						icon={faPencil}
					/>
				</button>
			</div>
			{invalidNameMessage && <div className="text-caption text-red-6">{invalidNameMessage}</div>}
		</form>
	);

	const Title = (
		<>
			<h2
				className="group flex items-center gap-[8px] text-neutral-9"
				onClick={() => {
					if (surveyUserAccess !== "edit") return;
					setIsEditingName(true);
					setters.setSurveyName(name);
				}}
			>
				<span className="max-w-[780px] overflow-hidden border-b-[0.5px] border-solid border-white text-sub-heading font-semi-bold whitespace-nowrap text-neutral-9 group-hover:border-neutral-9 hover:cursor-text">
					<Text
						wrap={false}
						data-testid="form-name"
						weight="semi-bold"
						size="sub-heading"
						dangerouslySetClassName="py-text-truncate"
					>
						{name}
					</Text>
				</span>
				{surveyUserAccess === "edit" && (
					<button
						data-testid="edit-question-button"
						className="border-0 bg-transparent p-0"
						onClick={() => {
							setIsEditingName(true);
							setters.setSurveyName(name);
						}}
						aria-label="Edit survey title"
					>
						<Icon
							className="text-neutral-5 hover:cursor-pointer hover:text-neutral-7"
							size="small"
							icon={faPencil}
						/>
					</button>
				)}
			</h2>
			{invalidNameMessage && <div className="text-caption text-red-6">{invalidNameMessage}</div>}
		</>
	);

	return <div>{isEditingName ? FormTitle : Title}</div>;
};

export default TitleInput;
